/**
 * The run behind the product shots' answer: its events in the shapes the
 * executor emits, and everything the product derives from them (the trace
 * lines, the update count, the "Retrieved with" sources). Kept apart from
 * fixture.ts so the shots' client chrome, which reads the workspace, never
 * loads the run-derivation code.
 */

import type { TaskRunEvent } from "@mlx/contracts";
import {
  formatUpdateCount,
  runActivityLabel,
  runFooterStatus,
} from "@product/components/app/runs/run-trace-labels";
import type { PluginIconPlugin } from "@product/components/content/plugin-icon";
import { formatRunDuration } from "@product/lib/run-details-format";
import { buildRunSourceUsage, type RunSourceUsage } from "@product/lib/run-source-usage";
import type { RenderedRunEventBlock } from "@product/lib/task-run-ui";

import { ANSWER_MARKDOWN, CHANNEL_NAME, DATA_SET_NAME, MINUTE, NOW, SAMPLES } from "./fixture";
import {
  answerDeltas,
  jsonResult,
  replayAnswerAt,
  replayBlocksAt,
  replayEvents,
  replayLiveUpdateAt,
  type ReplayLiveUpdate,
  type ReplayRun,
} from "./replay-run";

/** Asked at 14:01; the run takes 23 seconds. */
export const RUN_STARTED_AT = NOW - MINUTE;
export const RUN_DURATION_MS = 23_000;

/**
 * The tools the run called, as the executor names them: the Twin workspace
 * plugin's channel query, Xero's accounting read and a query over the
 * ingested Google Drive data (each plugin's own label and its tool's label,
 * from packages/runtime-plugins/catalogue/<slug>/plugin.json).
 */
const WORKSPACE_TOOL = {
  toolName: "twin_workspace_query_channel_records",
  toolLabel: "Query channel records",
  pluginSlug: "mlx-workspace",
  pluginLabel: "Twin workspace",
} as const;
const XERO_TOOL = {
  toolName: "xero_accounting_read",
  toolLabel: "Xero Accounting API read",
  pluginSlug: "xero",
  pluginLabel: "Xero",
} as const;
const DRIVE_TOOL = {
  toolName: "warehouse_query",
  toolLabel: "Query ingested Google Drive data",
  pluginSlug: "google-drive",
  pluginLabel: "Google Drive",
} as const;

/** The answer as the model streamed it, a word at a time. */
export const ANSWER_DELTAS: readonly string[] = answerDeltas(ANSWER_MARKDOWN);

export const RUN_ID = "run_autumn_kettles";

/**
 * The kettle run: queued and claimed, three tool calls, the streamed answer
 * and completion. The trace pill, the expanded trace and the "Retrieved
 * with" row are all derived from its events by the application's own
 * functions, never written by hand; the stored lines are what those
 * functions print (fixture.test.ts re-derives them at every point).
 */
export const OSTREY_RUN: ReplayRun = {
  id: RUN_ID,
  eventPrefix: "evt_kettles",
  startedAt: RUN_STARTED_AT,
  durationMs: RUN_DURATION_MS,
  steps: [
    ["run.queued", 0, { source: "web" }],
    ["run.claimed", 380, { workerId: "executor-2" }],
    ["tool.started", 1_200, { toolCallId: "call_samples", ...WORKSPACE_TOOL, args: { channel: CHANNEL_NAME, dataSet: DATA_SET_NAME, sampleIds: SAMPLES.map((sample) => sample.code) } }],
    ["tool.completed", 2_100, { toolCallId: "call_samples", ...WORKSPACE_TOOL, result: jsonResult({ records: SAMPLES.map((sample) => ({ id: sample.id, title: sample.code })) }) }],
    ["tool.started", 2_600, { toolCallId: "call_bills", ...XERO_TOOL, args: { method: "GET", path: "/Invoices", where: 'Type=="ACCPAY"' } }],
    ["tool.completed", 4_900, { toolCallId: "call_bills", ...XERO_TOOL, result: jsonResult({ Invoices: [{ Type: "ACCPAY", InvoiceNumber: "BW-2291" }, { Type: "ACCPAY", InvoiceNumber: "KM-0417" }] }) }],
    ["tool.started", 5_300, { toolCallId: "call_qc", ...DRIVE_TOOL, args: {} }],
    ["tool.updated", 5_600, { toolCallId: "call_qc", ...DRIVE_TOOL, args: {}, partialResult: { details: { reason: "Finding the QC reports for SMP-0412 and SMP-0419" } } }],
    ["tool.completed", 6_100, { toolCallId: "call_qc", ...DRIVE_TOOL, result: { ...jsonResult({ rows: 2 }), details: { rowCount: 2, durationMs: 84 } } }],
  ],
  stream: { startsMs: 7_400, endsMs: 22_600 },
  statusLines: {
    evt_kettles_001: { title: "Queued", description: "The prompt was stored in Postgres and is waiting for a worker.", tone: "warning" },
    evt_kettles_002: { title: "Claimed", description: "Claimed by executor-2.", tone: "info" },
    evt_kettles_003: { title: "Query channel records", description: "Query channel records started.", tone: "info" },
    evt_kettles_004: { title: "Query channel records", description: "Query channel records finished successfully.", tone: "success" },
    evt_kettles_005: { title: "Xero Accounting API read", description: "Xero Accounting API read started.", tone: "info" },
    evt_kettles_006: { title: "Xero Accounting API read", description: "Xero Accounting API read finished successfully.", tone: "success" },
    evt_kettles_008: { title: "Running a query", description: "Finding the QC reports for SMP-0412 and SMP-0419", tone: "info" },
    evt_kettles_009: { title: "Query finished", description: "2 rows in 84ms", tone: "success" },
    evt_kettles_133: { title: "Completed", description: "Final output was written back to Postgres.", tone: "success" },
  },
  liveUpdates: [
    { id: "evt_kettles_001", atMs: 0, text: "Waiting to start" },
    { id: "evt_kettles_002", atMs: 380, text: "Starting analysis" },
    { id: "evt_kettles_003", atMs: 1_200, text: "Checking channel records" },
    { id: "evt_kettles_004", atMs: 2_100, text: "Channel records checked" },
    { id: "evt_kettles_005", atMs: 2_600, text: "Checking Xero" },
    { id: "evt_kettles_006", atMs: 4_900, text: "Checked Xero" },
    { id: "evt_kettles_008", atMs: 5_600, text: "Searching Google Drive" },
    { id: "evt_kettles_009", atMs: 6_100, text: "Checked Google Drive" },
  ],
};

/** The run's events, in the shapes the executor emits. */
export const RUN_EVENTS: TaskRunEvent[] = replayEvents(OSTREY_RUN, ANSWER_MARKDOWN);

/** The trace's lines once the run is `atMs` in, as TaskRunActivity shows them. */
export function runBlocksAt(atMs: number): RenderedRunEventBlock[] {
  return replayBlocksAt(OSTREY_RUN, RUN_EVENTS, atMs);
}

/** The finished run's trace lines. */
export const RUN_BLOCKS: RenderedRunEventBlock[] = runBlocksAt(RUN_DURATION_MS);

/** The run's receipt: the sources it retrieved with, as RunSourcesUsed builds it. */
export const RUN_SOURCES: RunSourceUsage[] = buildRunSourceUsage(RUN_EVENTS);

/**
 * The runtime plugin catalogue's entries for the plugins the run used, with
 * the icon the app's catalogue names for each (its /icons registry, copied
 * to apps/web/public/concepts/product-shot/icons).
 */
export const RUN_PLUGINS: PluginIconPlugin[] = [
  { slug: "mlx-workspace", label: "Twin workspace", branding: { iconUrl: "/concepts/product-shot/icons/mlx/workspace.svg" } },
  { slug: "xero", label: "Xero", branding: { iconUrl: "/concepts/product-shot/icons/sources/xero.svg" } },
  { slug: "google-drive", label: "Google Drive", branding: { iconUrl: "/concepts/product-shot/icons/sources/google-drive.png" } },
];

export { formatUpdateCount };

/** The trace pill and footer, labelled as TaskRunActivity labels a finished run. */
export const RUN = {
  label: runActivityLabel(true),
  updateCountLabel: formatUpdateCount(RUN_BLOCKS.length),
  durationLabel: formatRunDuration(RUN_DURATION_MS),
  status: runFooterStatus("completed"),
} as const;

/** The pill and footer while the run works (TaskRunActivity's labels). */
export const RUNNING = { label: runActivityLabel(false), status: runFooterStatus("running") } as const;

/** The progress lines the transcript shows while the run works. */
export const RUN_LIVE_UPDATES: readonly ReplayLiveUpdate[] = OSTREY_RUN.liveUpdates;

/** When the answer starts and stops streaming, in run time. */
export const RUN_STREAM = OSTREY_RUN.stream;

/** The newest live update once the run is `atMs` in, or none once the answer streams. */
export function runLiveUpdateAt(atMs: number) {
  return replayLiveUpdateAt(OSTREY_RUN, atMs);
}

/** The answer as far as it has streamed once the run is `atMs` in. */
export function streamedAnswerAt(atMs: number): string {
  return replayAnswerAt(OSTREY_RUN, RUN_EVENTS, atMs);
}
